import React, { useState, useContext, useCallback, useMemo } from 'react';
import { withRouter } from 'react-router-dom';
import {
  TmoSvg,
  TmoInput,
  TmoSelect,
  withModal
} from '@tmobile/tmo-bps-syncup-web-component-lib';

import mobileAppService from '../../services/mobileAppService';
import Fetcher from '../Fetcher';
import SyncUpProductContext from '../../context/SyncUpProductContext';
import StatusContext from '../../context/StatusContext';
import useVisible from '../../hooks/useVisible';
import { getRandomString } from '../../utils/stringUtils';
import { APP_MOBILE } from '../../utils/app_constants';

import RatePlanHeader from './RatePlanHeader';
import RatePlanList from './RatePlanList';

import style from './DisplayRatePlans.module.css';

const baseRatePlanFormFields = [
  {
    labelText: 'Plan Name',
    fieldName: 'planName',
    formComponent: TmoInput,
    required: true,
    placeholder: 'Enter plan name'
  },
  {
    labelText: 'Product',
    fieldName: 'product',
    formComponent: TmoInput,
    required: true,
    placeholder: 'Enter product'
  }
];

const versionRatePlanFormField = {
  labelText: 'Version',
  fieldName: 'version',
  formComponent: TmoSelect,
  required: true,
  selectSpecs: {
    options: [{ value: 'V1', text: 'V1' }, { value: 'V2', text: 'V2' }]
  },
  defaultValue: 'V1'
};

function DisplayRatePlans({ match: { url }, openModal }) {
  const { syncUpAppName } = useContext(SyncUpProductContext);

  const ratePlanFormFields = useMemo(
    () =>
      syncUpAppName === APP_MOBILE.TRACKER
        ? [...baseRatePlanFormFields, versionRatePlanFormField]
        : baseRatePlanFormFields,
    [syncUpAppName]
  );
  const { addSuccessMessage, addErrorMessage } = useContext(StatusContext);
  const [dataFromChild, setDataFromChild] = useState();
  const [ratePlanList, setRatePlanList] = useState([]);
  const [refreshKey, setRefreshKey] = useState(
    () => getRandomString() + syncUpAppName
  );

  const { visible, setVisible } = useVisible(false);

  const forceRefresh = useCallback(() => {
    setRefreshKey(getRandomString());
  }, []);

  const actionCallback = useCallback(() => {
    return mobileAppService.getRatePlans({ syncUpAppName });
  }, [syncUpAppName]);

  const handleLoadData = useCallback(
    data => {
      let dataToUse = data.filter(p => p.product === syncUpAppName);
      setRatePlanList(dataToUse);
      if (dataFromChild && dataFromChild.length > 0) {
        const results = dataToUse.filter(item =>
          item.planName.toLowerCase().includes(dataFromChild.toLowerCase())
        );
        setRatePlanList(results);
      } else {
        setRatePlanList(dataToUse);
      }
    },
    [syncUpAppName, dataFromChild]
  );

  const openAddModal = useCallback(async () => {
    const { isConfirmed, formValues } = await openModal();
    if (!isConfirmed) return;

    setVisible(true);
    try {
      await mobileAppService.createRatePlan({
        ...formValues,
        syncUpAppName
      });
      addSuccessMessage({
        message: `Successfully added Rate Plan: ${formValues.planName}`
      });
      forceRefresh();
    } catch (error) {
      console.error('Failed to create rate plan:', error);
      addErrorMessage({ error });
    } finally {
      setVisible(false);
    }
  }, [
    openModal,
    syncUpAppName,
    addSuccessMessage,
    addErrorMessage,
    forceRefresh,
    setVisible
  ]);

  const handleDelete = useCallback(
    async id => {
      try {
        await mobileAppService.deleteRatePlan({
          ratePlanId: id,
          syncUpAppName
        });
        addSuccessMessage({ message: `Rate Plan deleted successfully` });
        forceRefresh();
      } catch (error) {
        console.error('Failed to delete rate plan:', error);
        addErrorMessage({ error });
      }
    },
    [syncUpAppName, addSuccessMessage, addErrorMessage, forceRefresh]
  );
  function handleDataFromChild(data) {
    setDataFromChild(data);
  }
  return (
    <>
      <RatePlanHeader
        count={ratePlanList.length}
        onAdd={openAddModal}
        formFieldSpecs={ratePlanFormFields}
        sendFilteredRes={handleDataFromChild}
      />

      {visible && <TmoSvg className={style.spinner} svgId="loading-spinner" />}

      <Fetcher
        key={refreshKey}
        action={actionCallback}
        onLoad={handleLoadData}
        render={() => (
          <RatePlanList
            ratePlans={ratePlanList}
            onDelete={handleDelete}
            action={actionCallback}
            product={syncUpAppName}
            forceRefresh={forceRefresh}
          />
        )}
      />
    </>
  );
}

export default withModal(withRouter(DisplayRatePlans));
